type MatomoCommand = [method: string, ...args: unknown[]]

export type MatomoEvent = {
  category: string
  action: string
  name?: string
  value?: number
}

const MATOMO_SCRIPT_ID = 'matomo-tracker-script'

declare global {
  interface Window {
    __matomoInitialized?: boolean
    _paq?: MatomoCommand[]
  }
}

function getMatomoConfig() {
  const enabled = process.env.NEXT_PUBLIC_MATOMO_ENABLED === 'true'
  const siteId = process.env.NEXT_PUBLIC_MATOMO_SITE_ID
  const url = process.env.NEXT_PUBLIC_MATOMO_URL

  return {
    enabled,
    siteId,
    url: url?.replace(/\/+$/, ''),
  }
}

function getQueue(): MatomoCommand[] {
  window._paq = window._paq ?? []
  return window._paq
}

function enqueue(command: MatomoCommand) {
  if (typeof window === 'undefined') return
  getQueue().push(command)
}

export function isMatomoEnabled() {
  const config = getMatomoConfig()
  return config.enabled && Boolean(config.siteId) && Boolean(config.url)
}

export function sanitizeTrackedPath(pathname: string) {
  if (!pathname) return '/'

  try {
    const parsedUrl = new URL(pathname, 'https://local.invalid')
    return parsedUrl.pathname || '/'
  } catch {
    return pathname.split('?')[0]?.split('#')[0] || '/'
  }
}

export function initMatomo() {
  if (typeof window === 'undefined') return
  if (!isMatomoEnabled()) return
  if (window.__matomoInitialized) return

  const { siteId, url } = getMatomoConfig()
  if (!siteId || !url) return

  enqueue(['disableCookies'])
  enqueue(['setTrackerUrl', `${url}/matomo.php`])
  enqueue(['setSiteId', siteId])
  enqueue(['enableLinkTracking'])

  const existingScript = document.getElementById(MATOMO_SCRIPT_ID)
  if (!existingScript) {
    const script = document.createElement('script')
    script.id = MATOMO_SCRIPT_ID
    script.async = true
    script.defer = true
    script.src = `${url}/matomo.js`

    document.head.appendChild(script)
  }

  window.__matomoInitialized = true
}

export function trackPageView(pathname: string, title?: string) {
  if (typeof window === 'undefined') return
  if (!isMatomoEnabled()) return

  const sanitizedPath = sanitizeTrackedPath(pathname)

  enqueue(['setCustomUrl', sanitizedPath])
  enqueue(['setDocumentTitle', title ?? document.title])
  enqueue(['deleteCustomVariables', 'page'])
  enqueue(['trackPageView'])
}

export function trackEvent({ category, action, name, value }: MatomoEvent) {
  if (typeof window === 'undefined') return
  if (!isMatomoEnabled()) return

  const command: MatomoCommand = ['trackEvent', category, action]

  if (name !== undefined) {
    command.push(name)
  }

  if (value !== undefined) {
    if (name === undefined) {
      command.push('')
    }
    command.push(value)
  }

  enqueue(command)
}
